import { FormControl, FormErrorMessage, Radio, RadioGroup, Spinner, Stack, useToken } from '@chakra-ui/react'
import type { BaseParamsVehicleDto } from 'BrokerTsLib/src/dtos'
import { Risk, RiskVariant } from 'BrokerTsLib/src/enums'
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

import { FormGroup } from '../../components/FormGroup'
import { FormLabel } from '../../components/FormLabel'
import { useGetRiskVariants } from '../../hooks'
import { useNameof } from '../../hooks/useNameof'
import { Hav, Liquidation, Pov } from '../../icons'

export const InsuranceType = ({ isContracted, propPath, showHav = true }: { isContracted: boolean, propPath?: string, showHav?: boolean }) => {
  const nameof = useNameof<BaseParamsVehicleDto>(propPath)

  const [secondaryColor] = useToken('colors', ['secondary.500'])
  const { control, watch, setValue, getFieldState } = useFormContext()

  const { data: dataPov, isLoading: isLoadingPov, isFetching: isFetchingPov } = useGetRiskVariants(Risk.POV)
  const { data: dataHav, isLoading: isLoadingHav, isFetching: isFetchingHav } = useGetRiskVariants(Risk.HAV)
  const { data: dataSpol, isLoading: isLoadingSpol, isFetching: isFetchingSpol } = useGetRiskVariants(Risk.SPOL)

  const { error: spolError } = getFieldState(nameof(x => x.spol))
  const { error: povError } = getFieldState(nameof(x => x.pov))
  const havArray = watch(nameof(x => x.hav)) ?? []
  const hav = havArray.length > 0 ? havArray[0] : RiskVariant.WithoutHAV

  useEffect(() => {
    if (hav.toString() === RiskVariant.WithoutHAV.toString())
      setValue(nameof(x => x.spol), [])
  }, [hav])

  return (
    <FormGroup size="lg">

      {/* baseParams.pov */}
      <FormControl isInvalid={!!povError} isRequired>
        <Pov secondary={secondaryColor} width={8} height={8} sx={{ mx: 'auto', mb: 2 }} />

        <Stack direction={'row'}>
          <FormLabel htmlFor="pov" tooltip="Vyberte požadovaný rozsah limitů povinného ručení">
            Povinné ručení
          </FormLabel>

          {(isFetchingPov || isLoadingPov) && <Spinner />}
        </Stack>
        <Controller
          name={nameof(x => x.pov)}
          control={control}
          render={({ field: { onChange, value } }) => (
            <RadioGroup
              onChange={e => onChange([parseInt(e)])}
              value={value && value.length > 0 ? value[0].toString() : ''}
              isDisabled={isContracted}
            >
              <Stack direction="column" spacing={4}>
                {dataPov?.map((item) => (
                  <Radio value={item.id.toString()} key={item.id}>
                    {item.name}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          )}
        />
        {povError && <FormErrorMessage>{povError.message}</FormErrorMessage>}
      </FormControl>

      {showHav &&
        <FormControl isRequired>
          <Hav secondary={secondaryColor} width={8} height={8} sx={{ mx: 'auto', mb: 2 }} />

          <Stack direction={'row'}>
            <FormLabel htmlFor={nameof(x => x.hav)} tooltip="Vyberte požadovaný rozsah havarijního pojištění. Allrisk = havárie, odcizení, živel, vandalismus.">
              Havarijní pojištění
            </FormLabel>

            {(isFetchingHav || isLoadingHav) && <Spinner />}
          </Stack>

          {/* baseParams.hav */}
          <Controller
            name={nameof(x => x.hav)}
            control={control}
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                onChange={e => {
                  onChange([parseInt(e)])
                  if (e === RiskVariant.WithoutHAV.toString())
                    setValue(nameof(x => x.spol), null)
                }}
                value={value && value.length > 0 ? value[0].toString() : ''}
                isDisabled={isContracted}
              >
                <Stack direction="column" spacing={4}>
                  {dataHav?.filter(x => x.id != RiskVariant.Package).map((item) => (
                    <Radio value={item.id.toString()} key={item.id}>
                      {item.name}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            )}
          />
        </FormControl>
      }

      {/* baseParams.spol */}
      {showHav &&
        <FormControl isInvalid={!!spolError}>
          <Liquidation secondary={secondaryColor} width={8} height={8} sx={{ mx: 'auto', mb: 2 }} />

          <Stack direction={'row'}>
            <FormLabel htmlFor={nameof(x => x.spol)} tooltip="Vyberte požadovanou spoluúčast k havarijnímu pojištění">
              Spoluúčast
            </FormLabel>

            {(isFetchingSpol || isLoadingSpol) && <Spinner />}
          </Stack>

          <Controller
            name={nameof(x => x.spol)}
            control={control}
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                onChange={e => onChange([parseInt(e)])}
                value={value && value.length > 0 ? value[0].toString() : ''}
                isDisabled={isContracted || hav.toString() === RiskVariant.WithoutHAV.toString()}
              >
                <Stack direction="column" spacing={4}>
                  {dataSpol?.map((item) => (
                    <Radio value={item.id.toString()} key={item.id.toString()}>
                      {item.name.replace('Spoluúčast', '')}
                    </Radio>
                  ))}
                </Stack>

                {spolError && <FormErrorMessage>{spolError.message}</FormErrorMessage>}
              </RadioGroup>
            )}
          />
        </FormControl>
      }
    </FormGroup>
  )
}
